'use client'
import { motion } from 'motion/react'
import Link from 'next/link'
import type { FC } from 'react'
import { IconComponent } from '@/components/Icon/Icon'
import type { SliceComponentProps, SliceLike } from '@/lib/rich-text'

type Button = { text: string; href: string; variant: 'Contained' | 'Text'; target?: '_blank' }

/**
 * The homepage pricing message: one starting price and the four things that
 * make it simple, with a link to the full pricing page. The tier table stays on
 * `/pricing`; the homepage should make pricing feel simple, not explain it.
 */
export interface PricingSummarySlice extends SliceLike<'pricing_summary'> {
  variation: 'default'
  primary: {
    eyebrow: string
    title: string
    price_prefix: string
    price: string
    price_suffix: string
    price_note?: string
    points: string[]
    buttons: Button[]
  }
}

export type PricingSummaryProps = SliceComponentProps<PricingSummarySlice>

const PricingSummary: FC<PricingSummaryProps> = ({ slice }) => {
  const { eyebrow, title, price_prefix, price, price_suffix, price_note, points, buttons } = slice.primary
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-light-gray py-20 md:py-28"
    >
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="grid grid-cols-1 items-center gap-10 overflow-hidden rounded-[28px] bg-white p-8 ring-1 ring-black/5 shadow-[0_1px_2px_rgba(45,3,59,0.06)] md:grid-cols-2 md:p-12 lg:p-16"
        >
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h2 className="display-title mt-3">{title}</h2>
            <p className="font-rubik text-ink mt-8 flex flex-wrap items-baseline gap-x-2 tracking-tight">
              <span className="text-[20px] font-medium text-[#4b4b4b]">{price_prefix}</span>
              <span className="text-[64px] leading-none font-semibold md:text-[80px]">{price}</span>
              <span className="text-[20px] font-medium text-[#4b4b4b]">{price_suffix}</span>
            </p>
            {price_note && <p className="mt-2 text-[14px] text-[#6b6b6b]">{price_note}</p>}
          </div>

          <div>
            <ul className="space-y-3">
              {points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-[17px] leading-7 text-[#2f2f2f]">
                  <span className="bg-primary/10 text-primary mt-1 flex size-5 shrink-0 items-center justify-center rounded-full">
                    <IconComponent icon="CheckMark" size="xsmall" className="size-3" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
            {buttons.length > 0 && (
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                {buttons.map((b) =>
                  b.variant === 'Contained' ? (
                    <a
                      key={b.text}
                      href={b.href}
                      target={b.target}
                      rel={b.target === '_blank' ? 'noopener noreferrer' : undefined}
                      className="bg-primary hover:bg-signal inline-flex items-center justify-center rounded-full px-7 py-3.5 text-[16px] font-semibold text-white shadow-[0_12px_40px_-10px_rgba(193,71,233,0.7)] transition"
                    >
                      {b.text}
                    </a>
                  ) : (
                    <Link
                      key={b.text}
                      href={b.href}
                      className="hover:text-primary inline-flex items-center justify-center gap-2 text-[16px] font-semibold text-[#2d033b] transition"
                    >
                      <IconComponent icon="ChevronRight" size="xsmall" className="text-primary" />
                      {b.text}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default PricingSummary
